import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  SHOPIFY_CONFIGURED,
  cartCreate,
  cartLinesAdd,
  cartLinesUpdate,
  cartLinesRemove,
} from "@/lib/shopify";
import { deriveBogoTier } from "@/lib/bogo";

const StoreContext = createContext(null);

const CART_KEY = "gg_cart";
const WISH_KEY = "gg_wishlist";
const SHOPIFY_CART_ID_KEY = "gg_shopify_cart_id";
const CHECKOUT_URL_KEY = "gg_checkout_url";

const readLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
  const autoCloseTimerRef = useRef(null);

  // Shopify-side cart state — mirrors the local cart so we can hand the
  // customer off to Shopify's secure checkout with one click.
  const [shopifyCartId, setShopifyCartId] = useState(() =>
    localStorage.getItem(SHOPIFY_CART_ID_KEY),
  );

  // Mini-cart drawer open/close state
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Migrate any old saved checkoutUrl that still points to a now-broken
  // primary domain (e.g. girlygrove.in pointing at the frontend, not Shopify).
  const [checkoutUrl, setCheckoutUrl] = useState(() => {
    const saved = localStorage.getItem(CHECKOUT_URL_KEY);
    if (!saved) return null;
    const shopifyHost = process.env.REACT_APP_SHOPIFY_DOMAIN;
    if (!shopifyHost) return saved;
    try {
      const u = new URL(saved);
      if (u.host !== shopifyHost) {
        u.host = shopifyHost;
        return u.toString();
      }
    } catch {
      /* ignore */
    }
    return saved;
  });
  const [cartSyncing, setCartSyncing] = useState(false);
  const [cartError, setCartError] = useState(null);

  // Map of localId → shopifyLineId so we can update/remove lines later.
  const lineMapRef = useRef({});

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    if (shopifyCartId) localStorage.setItem(SHOPIFY_CART_ID_KEY, shopifyCartId);
    else localStorage.removeItem(SHOPIFY_CART_ID_KEY);
  }, [shopifyCartId]);

  useEffect(() => {
    if (checkoutUrl) localStorage.setItem(CHECKOUT_URL_KEY, checkoutUrl);
    else localStorage.removeItem(CHECKOUT_URL_KEY);
  }, [checkoutUrl]);

  /* -------------------------------------------------------------- */
  /*  Sync helpers — converts current cart → Shopify cart           */
  /* -------------------------------------------------------------- */

  const refreshLineMap = (shopifyCart) => {
    const map = {};
    shopifyCart?.lines?.edges?.forEach((edge) => {
      const variantId = edge.node.merchandise?.id;
      if (variantId) map[variantId] = edge.node.id;
    });
    lineMapRef.current = map;
  };

  const ensureShopifyCart = useCallback(
    async (initialLines = []) => {
      if (!SHOPIFY_CONFIGURED) return null;
      try {
        const created = await cartCreate(initialLines);
        setShopifyCartId(created.id);
        setCheckoutUrl(created.checkoutUrl);
        refreshLineMap(created);
        return created;
      } catch (e) {
        setCartError(e.message);
        return null;
      }
    },
    [],
  );

  /* -------------------------------------------------------------- */
  /*  Local cart actions (UI-driven)                                */
  /* -------------------------------------------------------------- */

  const addToCart = useCallback(
    async (product, qty = 1, stayOpen = false) => {
      // 1) Update local UI cart immediately for snappy feel
      setCart((prev) => {
        const existing = prev.find((item) => item.id === product.id);
        if (existing) {
          return prev.map((item) =>
            item.id === product.id
              ? { ...item, qty: item.qty + qty }
              : item,
          );
        }
        return [
          ...prev,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            mrp: product.mrp,
            image: product.image,
            qty,
            variantId: product.variantId || null,
            bogoTier: deriveBogoTier(product),
            bundleId: product.bundleId || null,
            bundleName: product.bundleName || null,
            bundlePrice: product.bundlePrice || null,
            bundleQty: product.bundleQty || null,
            bundleHasGift: product.bundleHasGift || false,
          },
        ];
      });

      // Auto-open the drawer so the user sees the bag fill up,
      // then auto-close after 1.5s. Rapid adds reset the timer.
      setDrawerOpen(true);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      if (!stayOpen) {
        autoCloseTimerRef.current = setTimeout(() => {
          setDrawerOpen(false);
        }, 1500);
      }

      // Branded gold-bordered confirmation toast — single reusable id
      // so rapid adds replace the toast instead of stacking.
      toast(qty > 1 ? `Added to bag ✦ ×${qty}` : "Added to bag ✦", {
        id: "gg-add-to-bag",
        duration: 1500,
      });

      // 2) Sync to Shopify cart if we have a variant ID
      if (!SHOPIFY_CONFIGURED || !product.variantId) return;
      setCartSyncing(true);
      setCartError(null);
      try {
        const linesPayload = [
          { merchandiseId: product.variantId, quantity: qty },
        ];
        let updated;
        if (!shopifyCartId) {
          updated = await ensureShopifyCart(linesPayload);
        } else {
          // If line exists, update quantity instead of adding a duplicate.
          const existingLineId = lineMapRef.current[product.variantId];
          if (existingLineId) {
            const newQty =
              (cart.find((c) => c.id === product.id)?.qty || 0) + qty;
            updated = await cartLinesUpdate(shopifyCartId, [
              { id: existingLineId, quantity: newQty },
            ]);
          } else {
            updated = await cartLinesAdd(shopifyCartId, linesPayload);
          }
          if (updated) {
            setCheckoutUrl(updated.checkoutUrl);
            refreshLineMap(updated);
          }
        }
      } catch (e) {
        setCartError(e.message);
      } finally {
        setCartSyncing(false);
      }
    },
    [shopifyCartId, cart, ensureShopifyCart],
  );

  const removeFromCart = useCallback(
    async (id) => {
      const item = cart.find((c) => c.id === id);
      setCart((prev) => prev.filter((i) => i.id !== id));
      if (
        SHOPIFY_CONFIGURED &&
        shopifyCartId &&
        item?.variantId &&
        lineMapRef.current[item.variantId]
      ) {
        try {
          const updated = await cartLinesRemove(shopifyCartId, [
            lineMapRef.current[item.variantId],
          ]);
          setCheckoutUrl(updated.checkoutUrl);
          refreshLineMap(updated);
        } catch (e) {
          setCartError(e.message);
        }
      }
    },
    [cart, shopifyCartId],
  );

  const updateQty = useCallback(
    async (id, qty) => {
      const item = cart.find((c) => c.id === id);
      setCart((prev) =>
        prev
          .map((i) => (i.id === id ? { ...i, qty: Math.max(0, qty) } : i))
          .filter((i) => i.qty > 0),
      );
      if (
        SHOPIFY_CONFIGURED &&
        shopifyCartId &&
        item?.variantId &&
        lineMapRef.current[item.variantId]
      ) {
        try {
          let updated;
          if (qty <= 0) {
            updated = await cartLinesRemove(shopifyCartId, [
              lineMapRef.current[item.variantId],
            ]);
          } else {
            updated = await cartLinesUpdate(shopifyCartId, [
              {
                id: lineMapRef.current[item.variantId],
                quantity: qty,
              },
            ]);
          }
          setCheckoutUrl(updated.checkoutUrl);
          refreshLineMap(updated);
        } catch (e) {
          setCartError(e.message);
        }
      }
    },
    [cart, shopifyCartId],
  );

  const clearCart = useCallback(() => {
    setCart([]);
    setShopifyCartId(null);
    setCheckoutUrl(null);
    lineMapRef.current = {};
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  }, []);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);
  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.qty, 0);
  const cartMrpTotal = cart.reduce(
    (sum, i) => sum + (i.mrp || i.price) * i.qty,
    0,
  );

  return (
    <StoreContext.Provider
      value={{
        cart,
        wishlist,
        cartCount,
        cartTotal,
        cartMrpTotal,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        toggleWishlist,
        // Shopify cart
        checkoutUrl,
        cartSyncing,
        cartError,
        shopifyEnabled: SHOPIFY_CONFIGURED,
        // Drawer
        drawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
